// scheduled-campaign-edit.ts — every DECISION the scheduled-campaign editor
// makes, extracted from the component (#103).
//
// The jest env here is `node`: no DOM, no React Testing Library. A decision left
// inside `ScheduledCampaignEditModal` is a decision with no test, so the modal
// is deliberately reduced to "render what these functions return, send what
// `buildScheduledEditRequest` builds".
//
// Three jobs:
//   1. the date/time <-> `schedule.dueDate` conversion (the pickers speak
//      'dd/mm/yyyy' + 'HH:mm' in LOCAL time; the wire speaks ISO UTC),
//   2. what may be saved at all,
//   3. the request body — including the template MERGE, which is the one part
//      of this that has already been a real data-loss bug once.

import type { Template } from '@common/models/template';
import type { PendingCampaignEditRequest } from '@common-models/campaign-edit';
import type { DripStepTemplate } from '@common-reducers/DripEditorReducer';

import { sameAudienceIds } from './audience-selection';
import { mergeEditedTemplate } from './campaign-template-merge';

/** The two picker values, exactly as `BTDatePicker` / `BTTimePicker` emit them. */
export interface DateTimeFields {
    /** 'dd/mm/yyyy' */
    date: string;
    /** 'HH:mm' */
    time: string;
}

/**
 * Split a stored due date into the picker's two fields, in LOCAL time.
 *
 * Local, not UTC, deliberately: the user picked a wall-clock time and the
 * pickers show wall-clock time. Round-tripping through UTC here is how a
 * campaign scheduled for 09:00 comes back as 06:00 for anyone east of Greenwich
 * — and the customer who reported this issue is in Israel (UTC+3).
 */
export const toDateTimeFields = (at: Date | undefined): DateTimeFields => {
    if (!at || Number.isNaN(at.getTime())) return { date: '', time: '' };
    const pad = (n: number) => String(n).padStart(2, '0');
    return {
        date: `${pad(at.getDate())}/${pad(at.getMonth() + 1)}/${at.getFullYear()}`,
        time: `${pad(at.getHours())}:${pad(at.getMinutes())}`,
    };
};

/**
 * The inverse. `undefined` for anything that is not a complete, real local
 * date+time — never an `Invalid Date`, and never a silently rolled-over one.
 *
 * `new Date(2026, 1, 31)` is 3 March, not "31 February": the Date constructor
 * rolls overflow forward without complaining. So the parsed fields are read BACK
 * off the constructed date and compared. Without that, a typo'd 31/02 would
 * schedule a real send on a day the user never chose.
 */
export const fromDateTimeFields = ({ date, time }: DateTimeFields): Date | undefined => {
    const dateMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date.trim());
    const timeMatch = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
    if (!dateMatch || !timeMatch) return undefined;
    const [, dd, mm, yyyy] = dateMatch;
    const [, hh, min] = timeMatch;
    const day = Number(dd), month = Number(mm), year = Number(yyyy);
    const hour = Number(hh), minute = Number(min);
    if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
    if (hour > 23 || minute > 59) return undefined;
    const at = new Date(year, month - 1, day, hour, minute, 0, 0);
    if (Number.isNaN(at.getTime())) return undefined;
    // Reject the constructor's silent overflow (31/02 -> 03/03).
    if (at.getFullYear() !== year || at.getMonth() !== month - 1 || at.getDate() !== day) return undefined;
    return at;
};

/** Why Save is unavailable, or `undefined` when it is available. Rendered as an
 *  inline hint AND used to disable the button, so the two can never disagree. */
export type ScheduledEditBlocker =
    | 'loading'
    | 'no-audience'
    | 'invalid-time';

/**
 * Why Save is off, in the reader's language.
 *
 * `t` is a REQUIRED parameter rather than an optional one so the compiler names
 * every call site if this ever moves — the same discipline `deriveAudienceLabel`
 * and `unitLabel` use in this directory. `loading` deliberately has no sentence:
 * a spinner is not a blocker the user can act on.
 */
export const scheduledEditBlockerText = (
    blocker: ScheduledEditBlocker,
    t: (key: string, defaultValue: string) => string,
): string => {
    if (blocker === 'no-audience') return t('campaignsRedesign.scheduledEdit.blockerNoAudience', 'Choose at least one audience.');
    if (blocker === 'invalid-time') return t('campaignsRedesign.scheduledEdit.blockerInvalidTime', 'Choose a date and time to send.');
    return '';
};

/**
 * May this edit be saved?
 *
 * An empty audience is blocked HERE as well as server-side, because the server's
 * refusal arrives as an error the user has to read after losing the click,
 * whereas a disabled button with a reason next to it never gets there. The
 * server check is not redundant: it is the one that holds for any other caller.
 */
export const scheduledEditBlocker = (input: {
    editorHydrated: boolean;
    audienceIds: readonly string[];
    sendAt: Date | undefined;
}): ScheduledEditBlocker | undefined => {
    if (!input.editorHydrated) return 'loading';
    if (input.audienceIds.length === 0) return 'no-audience';
    if (!input.sendAt) return 'invalid-time';
    return undefined;
};

export interface ScheduledEditDraft {
    campaignId: string;
    /** `campaign.template` as loaded — the base of the merge. */
    storedTemplate: Template | null | undefined;
    /** `DripEditorReducer.steps[0].template`. Absent until the editor hydrates. */
    workingTemplate: DripStepTemplate | undefined;
    audienceIds: readonly string[];
    storedAudienceIds: readonly string[];
    sendAt: Date | undefined;
    storedSendAt: Date | undefined;
    /** FE#240: the working recurrence, and the campaign's stored recurrence, so
     *  the request only carries recurrence when it actually changed. */
    isRecurring?: boolean;
    rruleset?: string;
    storedIsRecurring?: boolean;
    storedRruleset?: string;
}

/**
 * The request body, or `null` when there is nothing sendable.
 *
 * THE TEMPLATE IS ALWAYS MERGED, NEVER SENT ALONE. `campaign-edit-pending`
 * patches `{template}` as a WHOLE FIELD, and the editor models only nine of its
 * keys (`drip-message-editor.tsx` rebuilds the working template as exactly `_id,
 * message, isPoll, poll, asset, imageId, sendLinkPreview, linkPreview, name`).
 * Sending the working copy alone therefore DELETES `buttons`, `templateType`,
 * `snippetShortcut` and every other inherited field. That was a real bug on the
 * tier-2 path; `campaign-template-merge.ts` is the fix and this is the second
 * caller of it.
 *
 * The template is always included, even untouched. A deep-equality check across
 * assets, polls and link previews would be a fresh source of bugs, and rewriting
 * the same merged object is a no-op — whereas the audience and the send time ARE
 * compared, so an untouched campaign is never told its audience changed and
 * `contactCount` is never needlessly invalidated server-side.
 */
export const buildScheduledEditRequest = (draft: ScheduledEditDraft): PendingCampaignEditRequest | null => {
    // Never build off a working copy this editor did not hydrate:
    // `DripEditorReducer` is a store-wide singleton, so before the campaign's
    // document loads `steps[0]` can belong to an entirely different campaign.
    if (!draft.workingTemplate) return null;

    const request: PendingCampaignEditRequest = {
        campaignId: draft.campaignId,
        template: mergeEditedTemplate(draft.storedTemplate, draft.workingTemplate),
    };

    const audience = draft.audienceIds.map(String);
    if (audience.length > 0 && !sameAudienceIds(audience, draft.storedAudienceIds.map(String))) {
        request.boardIDArray = audience;
    }

    if (draft.sendAt && draft.sendAt.getTime() !== draft.storedSendAt?.getTime()) {
        request.dueDate = draft.sendAt.toISOString();
    }

    // FE#240: recurrence — only sent when it changed. A live rule needs both
    // flags (the engine gate reads both); turning it off sends isRecurring:false
    // and clears the rule. Because the engine carries `schedule` forward, this is
    // "this and following occurrences".
    const recurring = Boolean(draft.isRecurring && draft.rruleset);
    const storedRecurring = Boolean(draft.storedIsRecurring && draft.storedRruleset);
    if (recurring !== storedRecurring || (recurring && draft.rruleset !== draft.storedRruleset)) {
        request.isRecurring = recurring;
        request.rruleset = recurring ? draft.rruleset : '';
    }

    return request;
};
